/**
 * WebhookExportConfig Component
 *
 * Configuration component for webhook auto-export.
 * Allows users to enable/disable webhook delivery per task.
 * The webhook URL is set at workspace level in the Integrations page.
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import cloudApi from "../../../../../services/cloudApi";

const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    } ${enabled ? "bg-indigo-500" : "bg-gray-300 dark:bg-gray-600"}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        enabled ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
);

const WebhookExportConfig = ({ config, onChange }) => {
  const navigate = useNavigate();
  const [integrationStatus, setIntegrationStatus] = useState(null);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const integrations = await cloudApi.getIntegrations();
        const wh = integrations.find((i) => i.provider === "webhook");
        setIntegrationStatus(wh?.status || "not_connected");
      } catch {
        setIntegrationStatus(null);
      }
    };
    checkConnection();
  }, []);

  const isConnected = integrationStatus === "active";

  return (
    <div className="space-y-2.5">
      {/* Connection status banner */}
      {!isConnected && integrationStatus !== null && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/20">
          <span className="text-amber-500 text-sm">!</span>
          <p className="text-xs text-amber-700 dark:text-amber-300 flex-1">
            Webhook is not connected.{" "}
            <button
              onClick={() => navigate("/dashboard/integrations")}
              className="underline font-medium hover:text-amber-800 dark:hover:text-amber-200"
            >
              Connect now
            </button>
          </p>
        </div>
      )}

      {/* Enable toggle */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Webhook Export</label>
            <p className="text-2xs text-gray-400 dark:text-gray-500">Receive a POST with a download link after each run</p>
          </div>
          <Toggle
            enabled={config.enabled}
            onChange={(enabled) => onChange({ ...config, enabled })}
            disabled={!isConnected}
          />
        </div>
      </div>

      {config.enabled && isConnected && (
        <p className="text-2xs text-gray-400 dark:text-gray-500 px-1">
          Webhook URL and signing secret are configured in the{" "}
          <button
            onClick={() => navigate("/dashboard/integrations")}
            className="text-indigo-500 dark:text-indigo-400 hover:underline"
          >
            Integrations page
          </button>
          .
        </p>
      )}
    </div>
  );
};

export default WebhookExportConfig;
